import React, { useState, useEffect } from 'react';
import { Upload, FileText, Download, Eye, Clock, AlertTriangle, CheckCircle, X, ArrowLeft, Loader, BarChart3, Trash2 } from 'lucide-react';
import { useAuth } from '../auth/AuthComponents';
import { useUsage } from '../../contexts/UsageContext';
import { API_BASE, apiRequest } from '../../utils/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import useIsMobile from '../../hooks/useIsMobile';
import SubscriptionNotification from '../shared/SubscriptionNotification';
import ProcessingDisclaimerModal, { useProcessingDisclaimer } from '../shared/ProcessingDisclaimerModal';
import ConfirmModal from '../shared/ConfirmModal';
import formatDocumentDisplayName from '../../utils/documentDisplayName';
import RichTextEditor from '../shared/RichTextEditor';
import { htmlToPlainText, richTextPlainLength, sanitizeRichHtml, plainTextToRichHtml } from '../../utils/richText';
import { COUNTRY_OPTIONS } from '../../constants/countries';
import { PROGRAM_TYPE_OPTIONS, PURPOSE_OPTIONS } from '../../constants/formOptions';
import FormSelect from '../shared/FormSelect';

function appendAnalysisContext(formData, contextData) {
  Object.entries(contextData).forEach(([key, value]) => {
    if (!value) return;
    if (key === 'jobRole') {
      formData.append('job_role', value);
    } else if (key === 'jobDescription') {
      formData.append('job_description', htmlToPlainText(value));
    } else if (key === 'course_description') {
      formData.append('course_description', htmlToPlainText(value));
    } else {
      formData.append(key, value);
    }
  });
}
// import { Modal } from '../shared/Modal'; // Assume you have a Modal or use a simple div for now

// CV Form Wizard Component
const CVFormWizard = ({ contextData, setContextData, currentStep, setCurrentStep, file, setFile, onComplete, analyzing, analyzeDisabled }) => {
  const cvSteps = [
    {
      id: 'jobRole',
      label: 'Job Role',
      placeholder: 'e.g., Senior Software Engineer',
      type: 'input',
      description: 'What position are you applying for?'
    },
    {
      id: 'jobDescription',
      label: 'Job Description',
      placeholder: 'Paste the complete job description here...',
      type: 'textarea',
      description: 'Paste the job posting details to optimize your CV accordingly.'
    },
    {
      id: 'fileUpload',
      label: 'Upload CV',
      type: 'file',
      description: 'Upload your CV/Resume for analysis.'
    }
  ];

  const totalSteps = cvSteps.length;
  const currentStepData = cvSteps[currentStep];
  const [showValidation, setShowValidation] = useState(false);
  const [isGeneratingJobDescription, setIsGeneratingJobDescription] = useState(false);

  const handleNext = () => {
    if (!isStepComplete(currentStepData.id)) {
      setShowValidation(true);
      return;
    }
    setShowValidation(false);
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex) => {
    setCurrentStep(stepIndex);
  };

  const isStepComplete = (stepId) => {
    if (stepId === 'fileUpload') {
      return file !== null;
    }
    if (stepId === 'jobDescription') {
      return richTextPlainLength(contextData[stepId]) > 0;
    }
    return contextData[stepId] && contextData[stepId].trim() !== '';
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && currentStepData.type === 'input') {
      e.preventDefault();
      if (isStepComplete(currentStepData.id) && currentStep < totalSteps - 1) {
        setShowValidation(false);
        handleNext();
      } else {
        setShowValidation(true);
      }
    }
  };

  const generateJobDescription = async () => {
    if (!contextData.jobRole || !contextData.jobRole.trim()) {
      alert('Please enter a job role first to generate a job description.');
      return;
    }

    setIsGeneratingJobDescription(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/generation/ai/generate-job-description`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('docxsai-token')}`
        },
        body: JSON.stringify({
          jobRole: contextData.jobRole
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to generate job description');
      }

      const data = await response.json();
      const richHtml = sanitizeRichHtml(data.jobDescription || '');
      setContextData(prev => ({
        ...prev,
        jobDescription: /<[a-z][\s\S]*>/i.test(richHtml) ? richHtml : plainTextToRichHtml(richHtml),
      }));
    } catch (error) {
      console.error('Error generating job description:', error);
      alert(`Failed to generate job description: ${error.message}`);
    } finally {
      setIsGeneratingJobDescription(false);
    }
  };

    return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center justify-center space-x-2 sm:space-x-4 mb-8">
        {cvSteps.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <button
              onClick={() => handleStepClick(index)}
              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold transition-all ${
                index === currentStep
                  ? 'bg-[var(--color-primary)] text-white ring-4 ring-[var(--color-primary-soft)]'
                  : isStepComplete(step.id)
                  ? 'bg-green-500 text-white hover:bg-green-600'
                  : index < currentStep
                  ? 'bg-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-border-subtle)]'
                  : 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)] hover:bg-[var(--color-border)]'
              }`}
            >
              {isStepComplete(step.id) ? (
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                index + 1
              )}
            </button>
            {index < totalSteps - 1 && (
              <div className={`w-8 sm:w-12 h-1 mx-1 sm:mx-2 rounded-full transition-all ${
                index < currentStep || isStepComplete(cvSteps[index + 1]?.id)
                  ? 'bg-[var(--color-primary)]'
                  : 'bg-[var(--color-border)]'
              }`} />
            )}
          </div>
        ))}
        </div>

      {/* Step Content */}
      <div className="elevated-card rounded-2xl p-4 sm:p-6">
        <div className="text-center mb-6">
          <h3 className="text-lg sm:text-xl font-semibold text-[var(--color-text)] mb-2">
            Step {currentStep + 1} of {totalSteps}
          </h3>
          <h4 className="text-base sm:text-lg text-[var(--color-primary)] mb-2">{currentStepData.label}</h4>
          <p className="text-[var(--color-text-muted)] text-sm">{currentStepData.description}</p>
      </div>

        <div className="space-y-4">
          {currentStepData.type === 'input' ? (
            <>
              <input
                type="text"
                value={contextData[currentStepData.id] || ''}
                onChange={(e) => {
                  setContextData(prev => ({ ...prev, [currentStepData.id]: e.target.value }));
                  setShowValidation(false);
                }}
                onKeyPress={handleKeyPress}
                placeholder={currentStepData.placeholder}
                className={`w-full px-4 py-3 bg-[var(--color-input-bg)] border rounded-lg text-[var(--color-text)] placeholder-[var(--color-placeholder)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] focus:border-[var(--color-primary)] text-base sm:text-lg ${showValidation && (!contextData[currentStepData.id] || !contextData[currentStepData.id].trim()) ? 'border-red-500' : 'border-[var(--color-border)]'}`}
                autoFocus
                required
              />
              {showValidation && (!contextData[currentStepData.id] || !contextData[currentStepData.id].trim()) && (
                <div className="text-red-400 text-xs mt-1">This field is required.</div>
              )}
            </>
          ) : currentStepData.type === 'textarea' ? (
            <div className="relative">
              <RichTextEditor
                value={contextData[currentStepData.id] || ''}
                onChange={(html) => {
                  setContextData(prev => ({ ...prev, [currentStepData.id]: html }));
                  setShowValidation(false);
                }}
                placeholder={currentStepData.placeholder}
                minHeight={200}
              />
              {currentStepData.id === 'jobDescription' ? (
                <div className="absolute top-2 right-2 z-10">
                  <button
                    type="button"
                    onClick={generateJobDescription}
                    disabled={isGeneratingJobDescription || !contextData.jobRole?.trim()}
                    className={`text-xs font-medium transition-colors px-2 py-1 rounded ${
                      isGeneratingJobDescription || !contextData.jobRole?.trim()
                        ? 'text-[var(--color-text-muted)] cursor-not-allowed bg-[var(--color-surface-muted)]'
                        : 'text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] hover:bg-[var(--color-primary-soft)]'
                    }`}
                  >
                    {isGeneratingJobDescription ? 'Generating...' : '🤖 Generate with AI'}
                  </button>
                </div>
              ) : null}
              {showValidation && !isStepComplete(currentStepData.id) ? (
                <div className="text-red-400 text-xs mt-1">This field is required.</div>
              ) : null}
            </div>
          ) : currentStepData.type === 'file' ? (
              <div>
              <input
                type="file"
                onChange={(e) => setFile(e.target.files[0])}
                accept=".pdf,.doc,.docx,.txt"
                className="hidden"
                id="cv-file-upload"
              />
              
              {!file ? (
                <div className="text-center">
                  <label
                    htmlFor="cv-file-upload"
                    className="inline-flex items-center px-6 py-3 bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white rounded-lg font-medium transition-colors cursor-pointer"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Upload CV
                  </label>
                  <p className="text-[var(--color-text-muted)] text-sm mt-2">PDF, DOC, DOCX or TXT files</p>
              </div>
              ) : (
                <div className="text-center">
                  <div className="bg-[var(--color-surface-muted)] border border-[var(--color-border)] rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-center mb-2">
                      <svg className="w-6 h-6 text-[var(--color-success)] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-[var(--color-success)] font-medium">File uploaded successfully</span>
                </div>
                    <p className="text-[var(--color-text)] font-semibold mb-1">{file.name}</p>
                    <p className="text-[var(--color-text-muted)] text-sm">
                      Size: {(file.size / 1024 / 1024).toFixed(2)} MB • Type: {file.type || 'Unknown'}
                    </p>
      </div>

                  <div className="flex justify-center space-x-3">
                    <label
                      htmlFor="cv-file-upload"
                      className="px-4 py-2 bg-[var(--color-surface-muted)] hover:bg-[var(--color-border)] text-[var(--color-text)] rounded-lg text-sm transition-colors cursor-pointer"
                    >
                      Replace File
                    </label>
            <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-sm transition-colors"
            >
                      Remove File
            </button>
                  </div>
          </div>
        )}
                </div>
          ) : null}
              </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center mt-6">
              <button 
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="px-4 sm:px-6 py-2 bg-[var(--color-surface-muted)] hover:bg-[var(--color-border)] disabled:bg-[var(--color-surface-muted)] disabled:text-[var(--color-text-muted)] disabled:cursor-not-allowed text-[var(--color-text)] rounded-lg font-medium transition-all flex items-center text-sm sm:text-base"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="hidden sm:inline">Previous</span>
            <span className="sm:hidden">Back</span>
              </button>

          <div className="text-center">
            <div className="text-xs sm:text-sm text-[var(--color-text-muted)]">
              Progress: {Math.round(((currentStep + (isStepComplete(currentStepData.id) ? 1 : 0)) / totalSteps) * 100)}%
            </div>
            <div className="w-24 sm:w-32 bg-[var(--color-border)] rounded-full h-2 mt-1">
              <div 
                className="bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${((currentStep + (isStepComplete(currentStepData.id) ? 1 : 0)) / totalSteps) * 100}%` 
                }}
              />
          </div>
        </div>

          <button
            onClick={currentStep === totalSteps - 1 ? onComplete : handleNext}
            disabled={!isStepComplete(currentStepData.id) || (currentStep === totalSteps - 1 && (analyzing || analyzeDisabled))}
            className="px-4 sm:px-6 py-2 bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] disabled:bg-[var(--color-surface-muted)] disabled:text-[var(--color-text-muted)] disabled:cursor-not-allowed text-[var(--color-text)] rounded-lg font-medium transition-all flex items-center text-sm sm:text-base"
          >
            {currentStep === totalSteps - 1 ? (
              analyzing ? (
                <svg className="animate-spin w-4 h-4 mr-2 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
              ) : null
            ) : null}
            <span className="hidden sm:inline">
              {currentStep === totalSteps - 1 ? (analyzing ? 'Analyzing...' : 'Analyze CV') : 'Next'}
            </span>
            <span className="sm:hidden">
              {currentStep === totalSteps - 1 ? (analyzing ? 'Analyzing...' : 'Analyze') : 'Next'}
            </span>
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          </div>
          
        {/* Quick Tips */}
        <div className="mt-6 p-4 bg-[var(--color-primary-soft)] border border-[var(--color-primary)]/20 rounded-lg">
          <h5 className="text-[var(--color-text)] text-sm font-medium mb-2">💡 Pro Tip:</h5>
          <p className="text-[var(--color-text-secondary)] text-sm">
            {currentStepData.id === 'jobRole' 
              ? 'Use the exact job title from the posting for better keyword matching. Press Enter to continue.'
              : currentStepData.id === 'jobDescription'
              ? 'Include requirements, responsibilities, and preferred qualifications for the most accurate analysis.'
              : 'Upload your most recent CV/Resume. Supported formats: PDF, DOC, DOCX, or TXT files.'
                            }
                          </p>
                    </div>
                    
        {/* Keyboard Shortcuts */}
        {currentStepData.type === 'input' && (
          <div className="mt-4 text-center">
            <p className="text-xs text-[var(--color-text-muted)]">
              Press <kbd className="px-1 py-0.5 bg-[var(--color-surface-muted)] rounded text-xs">Enter</kbd> to continue
            </p>
                            </div>
                      )}
                    </div>
    </div>
  );
};

// Contract Form Wizard Component
const ContractFormWizard = ({ file, setFile, onComplete, analyzing, analyzeDisabled }) => {
  const contractSteps = [
    {
      id: 'fileUpload',
      label: 'Upload Contract',
      type: 'file',
      description: 'Upload your contract document for analysis.'
    }
  ];

  const currentStepData = contractSteps[0]; // Only one step for contracts

    return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center justify-center space-x-2 sm:space-x-4 mb-8">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold transition-all bg-[var(--color-primary)] text-white ring-4 ring-[var(--color-primary-soft)]">
          1
          </div>
        </div>

      {/* Step Content */}
      <div className="elevated-card rounded-2xl p-4 sm:p-6">
        <div className="text-center mb-6">
          <h3 className="text-lg sm:text-xl font-semibold text-[var(--color-text)] mb-2">
            Step 1 of 1
          </h3>
          <h4 className="text-base sm:text-lg text-[var(--color-primary)] mb-2">{currentStepData.label}</h4>
          <p className="text-[var(--color-text-muted)] text-sm">{currentStepData.description}</p>
      </div>

        <div className="space-y-4">
          <div>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              accept=".pdf,.doc,.docx,.txt"
              className="hidden"
              id="contract-file-upload"
            />
            
            {!file ? (
              <div className="text-center">
                <label
                  htmlFor="contract-file-upload"
                  className="inline-flex items-center px-6 py-3 bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white rounded-lg font-medium transition-colors cursor-pointer"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Upload Contract
                </label>
                <p className="text-[var(--color-text-muted)] text-sm mt-2">PDF, DOC, DOCX or TXT files</p>
          </div>
            ) : (
              <div className="text-center">
                <div className="bg-[var(--color-surface-muted)] border border-[var(--color-border)] rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-center mb-2">
                    <svg className="w-6 h-6 text-[var(--color-success)] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-[var(--color-success)] font-medium">File uploaded successfully</span>
              </div>
                  <p className="text-[var(--color-text)] font-semibold mb-1">{file.name}</p>
                  <p className="text-[var(--color-text-muted)] text-sm">
                    Size: {(file.size / 1024 / 1024).toFixed(2)} MB • Type: {file.type || 'Unknown'}
                  </p>
            </div>
                
                <div className="flex justify-center space-x-3">
                  <label
                    htmlFor="contract-file-upload"
                    className="px-4 py-2 bg-[var(--color-surface-muted)] hover:bg-[var(--color-border)] text-[var(--color-text)] rounded-lg text-sm transition-colors cursor-pointer"
                  >
                    Replace File
                  </label>
            <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-sm transition-colors"
            >
                    Remove File
            </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-center mt-6">
              <button
            onClick={onComplete}
            disabled={!file || analyzing || analyzeDisabled}
            className="px-6 py-2 bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] disabled:bg-[var(--color-surface-muted)] disabled:text-[var(--color-text-muted)] disabled:cursor-not-allowed text-[var(--color-text)] rounded-lg font-medium transition-all flex items-center text-sm sm:text-base"
          >
            {analyzing ? (
              <svg className="animate-spin w-4 h-4 mr-2 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
            ) : null}
            <span>{analyzing ? 'Analyzing...' : 'Analyze Contract'}</span>
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
              </button>
        </div>

        {/* Quick Tips */}
        <div className="mt-6 p-4 bg-[var(--color-primary-soft)] border border-[var(--color-primary)]/20 rounded-lg">
          <h5 className="text-[var(--color-text)] text-sm font-medium mb-2">💡 Pro Tip:</h5>
          <p className="text-[var(--color-text-secondary)] text-sm">
            Upload contracts in PDF or DOC format for the most accurate risk assessment and clause analysis.
          </p>
        </div>
      </div>
    </div>
  );
};

const SOP_PROGRAM_TYPE_OPTIONS = PROGRAM_TYPE_OPTIONS;

// SOP Form Wizard Component  
const SOPFormWizard = ({ contextData, setContextData, currentStep, setCurrentStep, file, setFile, onComplete, analyzing, analyzeDisabled }) => {
  const sopSteps = [
    {
      id: 'program_type',
      label: 'Program Type',
      placeholder: 'Select program type',
      type: 'select',
      options: SOP_PROGRAM_TYPE_OPTIONS,
      description: 'What type of program are you applying for?'
    },
    {
      id: 'institution',
      label: 'Institution',
      placeholder: 'University name',
      type: 'input', 
      description: 'Which institution are you applying to?'
    },
    {
      id: 'course_of_study',
      label: 'Course of Study',
      placeholder: 'e.g., Computer Science',
      type: 'input',
      description: 'What is your intended course of study?'
    },
    {
      id: 'course_description',
      label: 'Course Description',
      placeholder: 'Paste key themes, modules, or the official course blurb...',
      type: 'textarea',
      description: 'Paste a short official blurb or key themes from the program page for better fit scoring.'
    },
    {
      id: 'years_of_experience',
      label: 'Years of Experience',
      placeholder: 'e.g., 3',
      type: 'input',
      description: 'How many years of relevant experience do you have?'
    },
    {
      id: 'fileUpload',
      label: 'Upload SOP',
      type: 'file',
      description: 'Upload your Statement of Purpose document.'
    }
  ];

  const totalSteps = sopSteps.length;
  const currentStepData = sopSteps[currentStep];
  const [showValidation, setShowValidation] = useState(false);

  const handleNext = () => {
    if (!isStepComplete(currentStepData.id)) {
      setShowValidation(true);
      return;
    }
    setShowValidation(false);
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex) => {
    setCurrentStep(stepIndex);
  };

  const isStepComplete = (stepId) => {
    if (stepId === 'fileUpload') {
      return file !== null;
    }
    if (stepId === 'course_description') {
      return richTextPlainLength(contextData[stepId]) >= 40;
    }
    const value = (contextData[stepId] || '').trim();
    if (!value) return false;
    return true;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && currentStepData.type === 'input') {
      e.preventDefault();
      if (isStepComplete(currentStepData.id) && currentStep < totalSteps - 1) {
        setShowValidation(false);
        handleNext();
      } else {
        setShowValidation(true);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center justify-center space-x-2 sm:space-x-4 mb-8">
        {sopSteps.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <button
              onClick={() => handleStepClick(index)}
              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold transition-all ${
                index === currentStep
                  ? 'bg-[var(--color-primary)] text-white ring-4 ring-[var(--color-primary-soft)]'
                  : isStepComplete(step.id)
                  ? 'bg-green-500 text-white hover:bg-green-600'
                  : index < currentStep
                  ? 'bg-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-border-subtle)]'
                  : 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)] hover:bg-[var(--color-border)]'
              }`}
            >
              {isStepComplete(step.id) ? (
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                index + 1
              )}
            </button>
            {index < totalSteps - 1 && (
              <div className={`w-8 sm:w-12 h-1 mx-1 sm:mx-2 rounded-full transition-all ${
                index < currentStep || isStepComplete(sopSteps[index + 1]?.id)
                  ? 'bg-[var(--color-primary)]'
                  : 'bg-[var(--color-border)]'
              }`} />
            )}
              </div>
        ))}
              </div>

      {/* Step Content */}
      <div className="elevated-card rounded-2xl p-4 sm:p-6">
        <div className="text-center mb-6">
          <h3 className="text-lg sm:text-xl font-semibold text-[var(--color-text)] mb-2">
            Step {currentStep + 1} of {totalSteps}
          </h3>
          <h4 className="text-base sm:text-lg text-[var(--color-primary)] mb-2">{currentStepData.label}</h4>
          <p className="text-[var(--color-text-muted)] text-sm">{currentStepData.description}</p>
              </div>

        <div className="space-y-4">
          {currentStepData.type === 'select' ? (
            <>
              <FormSelect
                value={contextData[currentStepData.id] || ''}
                onChange={(v) => {
                  setContextData(prev => ({ ...prev, [currentStepData.id]: v }));
                  setShowValidation(false);
                }}
                options={currentStepData.options || []}
                placeholder={currentStepData.placeholder || 'Select...'}
                error={showValidation && (!contextData[currentStepData.id] || !contextData[currentStepData.id].trim())}
              />
              {showValidation && (!contextData[currentStepData.id] || !contextData[currentStepData.id].trim()) && (
                <div className="text-red-400 text-xs mt-1">This field is required.</div>
              )}
            </>
          ) : currentStepData.type === 'textarea' ? (
            <>
              <RichTextEditor
                value={contextData[currentStepData.id] || ''}
                onChange={(html) => {
                  setContextData(prev => ({ ...prev, [currentStepData.id]: html }));
                  setShowValidation(false);
                }}
                placeholder={currentStepData.placeholder}
                minHeight={200}
              />
              {showValidation && !isStepComplete(currentStepData.id) && (
                <div className="text-red-400 text-xs mt-1">
                  Please add a fuller course description (at least a short paragraph).
                </div>
              )}
            </>
          ) : currentStepData.type === 'input' ? (
            <>
              <input
                type={currentStepData.id === 'years_of_experience' ? 'number' : 'text'}
                min={currentStepData.id === 'years_of_experience' ? '0' : undefined}
                value={contextData[currentStepData.id] || ''}
                onChange={(e) => {
                  setContextData(prev => ({ ...prev, [currentStepData.id]: e.target.value }));
                  setShowValidation(false);
                }}
                onKeyPress={handleKeyPress}
                placeholder={currentStepData.placeholder}
                className={`w-full px-4 py-3 bg-[var(--color-input-bg)] border rounded-lg text-[var(--color-text)] placeholder-[var(--color-placeholder)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] focus:border-[var(--color-primary)] text-base sm:text-lg ${showValidation && (!contextData[currentStepData.id] || !contextData[currentStepData.id].trim()) ? 'border-red-500' : 'border-[var(--color-border)]'}`}
                autoFocus
                required
              />
              {showValidation && (!contextData[currentStepData.id] || !contextData[currentStepData.id].trim()) && (
                <div className="text-red-400 text-xs mt-1">This field is required.</div>
              )}
            </>
          ) : currentStepData.type === 'file' ? (
            <div>
              <input
                type="file"
                onChange={(e) => setFile(e.target.files[0])}
                accept=".pdf,.doc,.docx,.txt"
                className="hidden"
                id="sop-file-upload"
              />
              
              {!file ? (
                <div className="text-center">
                  <label
                    htmlFor="sop-file-upload"
                    className="inline-flex items-center px-6 py-3 bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white rounded-lg font-medium transition-colors cursor-pointer"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Upload SOP
                  </label>
                  <p className="text-[var(--color-text-muted)] text-sm mt-2">PDF, DOC, DOCX or TXT files</p>
            </div>
              ) : (
                <div className="text-center">
                  <div className="bg-[var(--color-surface-muted)] border border-[var(--color-border)] rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-center mb-2">
                      <svg className="w-6 h-6 text-[var(--color-success)] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-[var(--color-success)] font-medium">File uploaded successfully</span>
            </div>
                    <p className="text-[var(--color-text)] font-semibold mb-1">{file.name}</p>
                    <p className="text-[var(--color-text-muted)] text-sm">
                      Size: {(file.size / 1024 / 1024).toFixed(2)} MB • Type: {file.type || 'Unknown'}
                    </p>
        </div>

                  <div className="flex justify-center space-x-3">
                    <label
                      htmlFor="sop-file-upload"
                      className="px-4 py-2 bg-[var(--color-surface-muted)] hover:bg-[var(--color-border)] text-[var(--color-text)] rounded-lg text-sm transition-colors cursor-pointer"
                    >
                      Replace File
                    </label>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-sm transition-colors"
                    >
                      Remove File
                    </button>
            </div>
            </div>
              )}
          </div>
          ) : null}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center mt-6">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="px-4 sm:px-6 py-2 bg-[var(--color-surface-muted)] hover:bg-[var(--color-border)] disabled:bg-[var(--color-surface-muted)] disabled:text-[var(--color-text-muted)] disabled:cursor-not-allowed text-[var(--color-text)] rounded-lg font-medium transition-all flex items-center text-sm sm:text-base"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="hidden sm:inline">Previous</span>
            <span className="sm:hidden">Back</span>
          </button>

          <div className="text-center">
            <div className="text-xs sm:text-sm text-[var(--color-text-muted)]">
              Progress: {Math.round(((currentStep + (isStepComplete(currentStepData.id) ? 1 : 0)) / totalSteps) * 100)}%
            </div>
            <div className="w-24 sm:w-32 bg-[var(--color-border)] rounded-full h-2 mt-1">
              <div 
                className="bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${((currentStep + (isStepComplete(currentStepData.id) ? 1 : 0)) / totalSteps) * 100}%` 
                }}
              />
            </div>
          </div>

          <button
            onClick={currentStep === totalSteps - 1 ? onComplete : handleNext}
            disabled={!isStepComplete(currentStepData.id) || (currentStep === totalSteps - 1 && (analyzing || analyzeDisabled))}
            className="px-4 sm:px-6 py-2 bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] disabled:bg-[var(--color-surface-muted)] disabled:text-[var(--color-text-muted)] disabled:cursor-not-allowed text-[var(--color-text)] rounded-lg font-medium transition-all flex items-center text-sm sm:text-base"
          >
            {currentStep === totalSteps - 1 ? (
              analyzing ? (
                <svg className="animate-spin w-4 h-4 mr-2 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
              ) : null
            ) : null}
            <span className="hidden sm:inline">
              {currentStep === totalSteps - 1 ? (analyzing ? 'Analyzing...' : 'Analyze SOP') : 'Next'}
            </span>
            <span className="sm:hidden">
              {currentStep === totalSteps - 1 ? (analyzing ? 'Analyzing...' : 'Analyze') : 'Next'}
            </span>
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
      </div>

        {/* Quick Tips */}
        <div className="mt-6 p-4 bg-[var(--color-primary-soft)] border border-[var(--color-primary)]/20 rounded-lg">
          <h5 className="text-[var(--color-text)] text-sm font-medium mb-2">💡 Pro Tip:</h5>
          <p className="text-[var(--color-text-secondary)] text-sm">
            {currentStepData.id === 'program_type' 
              ? 'Choose the degree level (Masters, PhD, etc.) for more accurate SOP feedback.'
              : currentStepData.id === 'institution'
              ? 'Include the full university name for tailored recommendations.'
              : currentStepData.id === 'course_of_study'
              ? 'State your intended course clearly for more tailored feedback.'
              : currentStepData.id === 'course_description'
              ? 'Paste themes from the official program page so fit scoring maps to real curriculum language.'
              : currentStepData.id === 'years_of_experience'
              ? 'Mention only relevant years of experience for your application.'
              : 'Upload your complete SOP draft for comprehensive feedback and improvement suggestions.'
            }
          </p>
        </div>

        {/* Keyboard Shortcuts */}
        {currentStepData.type === 'input' && (
          <div className="mt-4 text-center">
            <p className="text-xs text-[var(--color-text-muted)]">
              Press <kbd className="px-1 py-0.5 bg-[var(--color-surface-muted)] rounded text-xs">Enter</kbd> to continue
            </p>
        </div>
                )}
              </div>
          </div>
  );
};

const DocumentAnalyzer = ({ setCurrentPage, initialView = 'upload', initialDocumentType = 'contract' }) => {
  const { user, isAuthenticated } = useAuth();
  const { refreshUsage } = useUsage();
  const [documentType, setDocumentType] = useState(initialDocumentType);
  const [contextData, setContextData] = useState({});
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(initialView); // 'upload', 'history', 'analysis'
  const [documents, setDocuments] = useState([]);
  const [deletingDocId, setDeletingDocId] = useState(null);
  const [docToDelete, setDocToDelete] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [currentCvStep, setCurrentCvStep] = useState(0);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [usage, setUsage] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadDisabled, setUploadDisabled] = useState(false);
  const [analyzeDisabled, setAnalyzeDisabled] = useState(false);
  const [usageError, setUsageError] = useState('');
  const [showValidation, setShowValidation] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showRevampModal, setShowRevampModal] = useState(false);
  const [revampLoading, setRevampLoading] = useState(false);
  const [revampError, setRevampError] = useState('');
  const [revampForm, setRevampForm] = useState({
    purpose: '',
    guide_statement: '',
    program_type: '',
    university: '',
    country: '',
    course_title: '',
    course_description: '',
    style: '',
  });
  const [analysisTab, setAnalysisTab] = useState('formatting');
  const {
    open: disclaimerOpen,
    requestProcessing,
    confirmProcessing,
    dismissDisclaimer,
  } = useProcessingDisclaimer();

  const analyzeActionLabel =
    documentType === 'cv'
      ? 'Analyze CV'
      : documentType === 'sop'
        ? 'Analyze SOP'
        : 'Analyze Contract';

  useEffect(() => {
    // Fetch usage info on mount
    const fetchUsage = async () => {
      try {
        const usageData = await apiRequest('subscription/usage');
        setUsage(usageData);
        if ((usageData.current_month?.total_documents || 0) >= (usageData.monthly_limit || 0)) {
          setUploadDisabled(true);
          setAnalyzeDisabled(true);
          setUsageError('You have reached your free plan limit. Upgrade your subscription to analyze more documents.');
        } else {
          setUploadDisabled(false);
          setAnalyzeDisabled(false);
          setUsageError('');
        }
      } catch (err) {
        setUsageError('Unable to fetch usage info. Please refresh the page.');
      }
    };
    fetchUsage();
  }, []);

  // Always fetch documents when view changes to 'history'
  useEffect(() => {
    if (view === 'history') {
      fetchDocuments();
    }
  }, [view]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const data = await apiRequest('documents/history');
      if (data) {
        setDocuments(data.documents);
      }
    } catch (error) {
      toast.error('Failed to fetch document history.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      toast.success(`${selectedFile.name} selected`);
    }
  };

  // Helper to check if quota is reached
  const isQuotaReached = () => {
    return usage && (usage.current_month?.total_documents || 0) >= (usage.monthly_limit || 0);
  };

  // Updated file upload handler
  const handleFileUpload = async (e) => {
    if (isQuotaReached()) {
      setUsageError('You have reached your free plan limit. Upgrade your subscription to analyze more documents.');
      setUploadDisabled(true);
      setAnalyzeDisabled(true);
      toast.error('You have reached your free plan limit. Upgrade your subscription to analyze more documents.');
      return;
    }
    if (!file) {
      toast.error('Please select a file first.');
      return;
    }
    setLoading(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);

    // Append context data based on document type
    appendAnalysisContext(formData, contextData);

    try {
      // Simulate progress for UI feedback
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      // Use endpoint without leading slash
      const result = await apiRequest('documents/analyze', {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result) {
        setAnalysisResult(result);
        setView('analysis');
        toast.success('Analysis complete!');
      } else {
        throw new Error('Analysis failed to return a result.');
      }
    } catch (error) {
      const errorMessage = error.message || 'Analysis failed. Please try again.';
      toast.error(errorMessage);
      setError(errorMessage);
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDocument = async (doc) => {
    setLoading(true);
    try {
      // Fetch the full document details from the backend
      const fullDoc = await apiRequest(`documents/${doc.id}`);
      setSelectedDocument(fullDoc);
      setAnalysisResult(fullDoc);
      setView('analysis');
    } catch (error) {
      toast.error('Failed to load analysis result.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = (doc) => {
    setDocToDelete(doc);
  };

  const confirmDeleteDocument = async () => {
    if (!docToDelete) return;
    const doc = docToDelete;
    setDeletingDocId(doc.id);
    try {
      await apiRequest(`documents/${doc.id}`, { method: 'DELETE' });
      setDocuments((prev) => prev.filter((item) => item.id !== doc.id));
      if (selectedDocument?.id === doc.id || analysisResult?.id === doc.id) {
        setSelectedDocument(null);
        setAnalysisResult(null);
      }
      toast.success('Document deleted.');
    } catch (error) {
      toast.error(error?.message || 'Failed to delete document.');
    } finally {
      setDeletingDocId(null);
      setDocToDelete(null);
    }
  };

  // Updated analyze handler
  const handleAnalyze = async () => {
    if (isQuotaReached()) {
      setUsageError('You have reached your free plan limit. Upgrade your subscription to analyze more documents.');
      setAnalyzeDisabled(true);
      toast.error('You have reached your free plan limit. Upgrade your subscription to analyze more documents.');
      return;
    }
    setAnalyzing(true);
    setAnalyzeDisabled(true);
    try {
      // Use the same logic as handleFileUpload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      appendAnalysisContext(formData, contextData);
      const result = await apiRequest('documents/analyze', {
        method: 'POST',
        body: formData,
      });
      if (result) {
        setAnalysisResult(result);
        setView('analysis');
        toast.success('Analysis complete!');
        // Refresh usage data after successful analysis
        await refreshUsage();
      } else {
        throw new Error('Analysis failed to return a result.');
      }
    } catch (err) {
      let message = 'An error occurred while analyzing the document.';
      if (err?.response?.status === 403 || err?.response?.status === 429) {
        message = 'You have reached your subscription limit. Please upgrade your plan to continue.';
      } else if (err?.response?.data?.detail) {
        message = err.response.data.detail;
      } else if (err?.message && (err.message.toLowerCase().includes('limit') || err.message.toLowerCase().includes('quota'))) {
        message = 'You have reached your subscription limit. Please upgrade your plan to continue.';
      } else if (err?.message && err.message.toLowerCase().includes('failed to fetch')) {
        message = 'Unable to connect to the server. Please check your internet connection or try again later.';
      } else if (err?.message) {
        message = err.message;
      }
      toast.error(message);
    } finally {
      setAnalyzing(false);
      setAnalyzeDisabled(isQuotaReached());
    }
  };

  // Export PDF handler
  const handleExportPDF = async () => {
    try {
      const docId = analysisResult.id;
      if (!docId) {
        toast.error('No document selected for export.');
        return;
      }
      
      const token = localStorage.getItem('docxsai-token');
      const response = await fetch(`${API_BASE}/documents/${docId}/export-pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('PDF export error:', response.status, errorText);
        throw new Error(`Failed to export PDF: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = analysisResult.original_filename || analysisResult.filename || 'analysis.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exported successfully!');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error(err.message || 'Failed to export PDF.');
    }
  };

  // Revamp handler
  const handleRevamp = async () => {
    setRevampLoading(true);
    setRevampError('');
    try {
      const docId = analysisResult.id;
      if (!docId) throw new Error('No document selected for revamp.');
      const body = {
        purpose: revampForm.purpose,
        guide_statement: revampForm.guide_statement,
        style: revampForm.style,
      };
      if (documentType === 'sop') {
        body.program_type = revampForm.program_type;
        body.university = revampForm.university;
        body.country = revampForm.country;
        body.course_title = revampForm.course_title;
        body.course_description = htmlToPlainText(revampForm.course_description);
      }
      const result = await apiRequest(`documents/${docId}/revamp`, {
        method: 'POST',
        body,
      });
      setShowRevampModal(false);
      setRevampLoading(false);
      setRevampError('');
      toast.success('Document revamped successfully!');
      // Refresh usage data after successful revamp
      await refreshUsage();
      // Redirect to result page (to be implemented)
      navigate(`/revamp-result/${docId}`);
    } catch (err) {
      setRevampError(err.message || 'Revamp failed.');
      setRevampLoading(false);
    }
  };

  const renderDocumentTypeFields = () => {
    if (documentType === 'cv') {
  return (
        <CVFormWizard 
          contextData={contextData}
          setContextData={setContextData}
          currentStep={currentCvStep}
          setCurrentStep={setCurrentCvStep}
          file={file}
          setFile={setFile}
          onComplete={() => requestProcessing(handleAnalyze)}
          analyzing={analyzing}
          analyzeDisabled={analyzeDisabled}
        />
      );
    }
    if (documentType === 'contract') {
      return (
        <ContractFormWizard 
          file={file}
          setFile={setFile}
          onComplete={() => requestProcessing(handleAnalyze)}
          analyzing={analyzing}
          analyzeDisabled={analyzeDisabled}
        />
      );
    }
    if (documentType === 'sop') {
    return (
        <SOPFormWizard 
          contextData={contextData}
          setContextData={setContextData}
          currentStep={currentCvStep}
          setCurrentStep={setCurrentCvStep}
          file={file}
          setFile={setFile}
          onComplete={() => requestProcessing(handleAnalyze)}
          analyzing={analyzing}
          analyzeDisabled={analyzeDisabled}
        />
      );
    }
    return null;
  };

  const renderUploadView = () => (
    <div className="relative">
      <div className="w-full">
        <div className="mb-6 px-4">
          <h1 className="text-3xl font-bold text-[var(--color-text)] mb-4">
            Document{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">
              Analysis
            </span>
          </h1>
          <p className="text-[var(--color-text-muted)]">Upload your documents and get instant AI-powered insights, risk assessments, and optimization suggestions.</p>
          
          {/* Subscription Notification */}
          <div className="mt-4">
            <SubscriptionNotification />
          </div>
          {window.innerWidth < 768 && (
            <div className="mt-4">
              <button
                onClick={() => navigate('/recent-activities')}
                className="bg-[var(--color-surface)] border border-[var(--color-border)] hover:bg-[var(--color-surface-muted)] text-[var(--color-text)] px-6 py-2 rounded-lg font-medium transition-colors w-full"
              >
                View History
              </button>
            </div>
          )}
        </div>
        {/* View History Button - Top Right for desktop/tablet */}
        {window.innerWidth >= 768 && (
          <div className="absolute top-4 right-4 z-10">
            <button
              onClick={() => navigate('/recent-activities')}
              className="bg-[var(--color-surface)] border border-[var(--color-border)] hover:bg-[var(--color-surface-muted)] text-[var(--color-text)] px-6 py-2 rounded-lg font-medium transition-colors"
            >
              View History
            </button>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-500/40 rounded-lg flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-400 mr-3" />
            <span className="text-[var(--color-danger)]">{error}</span>
          </div>
        )}



        {/* Document Type Selection */}
        <div className="elevated-card rounded-lg p-6 mb-6 mx-4">
          <h2 className="text-xl font-semibold text-[var(--color-text)] mb-4">Select Document Type</h2>
          <div className={`${window.innerWidth < 1024 ? 'flex flex-row gap-4 overflow-x-auto pb-2' : 'grid grid-cols-1 md:grid-cols-3 gap-4'}`}
            style={window.innerWidth < 1024 ? { WebkitOverflowScrolling: 'touch' } : {}}>
            {[
              { id: 'contract', title: 'Contract Analysis', description: 'Identify risks and improve contract terms', icon: FileText },
              { id: 'cv', title: 'CV Analysis', description: 'Optimize your resume for specific job roles', icon: FileText },
              { id: 'sop', title: 'SOP Enhancement', description: 'Perfect your statement of purpose', icon: FileText }
            ].map((type) => (
                <button
                key={type.id}
                onClick={() => {
                  setDocumentType(type.id);
                  setError('');
                  setFile(null);
                  setContextData({});
                  setCurrentCvStep(0);
                }}
                className={`p-4 min-w-[240px] rounded-lg border-2 transition-all ${
                  documentType === type.id
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft)]'
                    : 'border-[var(--color-border)] hover:border-[var(--color-border-subtle)] bg-[var(--color-surface-muted)]'
                }`}
              >
                <type.icon className="w-8 h-8 text-[var(--color-primary)] mb-3" />
                <h3 className="text-[var(--color-text)] font-medium mb-2">{type.title}</h3>
                <p className="text-[var(--color-text-muted)] text-sm">{type.description}</p>
                </button>
            ))}
          </div>
        </div>

        {/* Context Fields */}
        {documentType && (
          <div className="elevated-card rounded-lg p-6 mb-6 mx-4">
            {renderDocumentTypeFields()}
          </div>
        )}
      </div>
    </div>
  );

  const renderHistoryView = () => (
    <div className={isMobile ? "p-2" : "p-4"}>
      <div className="max-w-full mx-auto">
        <div className={isMobile ? "flex flex-col gap-2 mb-4" : "flex items-center justify-between mb-6"}>
          <div>
            <h1 className={isMobile ? "text-xl font-bold text-[var(--color-text)] mb-1" : "text-3xl font-bold text-[var(--color-text)] mb-2"}>Document History</h1>
            <p className={isMobile ? "text-[var(--color-text-muted)] text-sm" : "text-[var(--color-text-muted)]"}>View and manage your analyzed documents</p>
          </div>
          <button
            onClick={() => setView('upload')}
            className={isMobile ? "w-full mt-2 bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white px-3 py-2 rounded-lg font-medium transition-colors" : "bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white px-4 py-2 rounded-lg font-medium transition-colors"}
          >
            New Analysis
          </button>
        </div>

        <div className={isMobile ? "elevated-card rounded-lg overflow-hidden" : "elevated-card rounded-lg overflow-hidden"}>
          <div className={isMobile ? "px-3 py-2 border-b border-[var(--color-border)]" : "px-6 py-4 border-b border-[var(--color-border)]"}>
            <h2 className={isMobile ? "text-base font-semibold text-[var(--color-text)]" : "text-lg font-semibold text-[var(--color-text)]"}>Recent Documents</h2>
          </div>
          {documents.length > 0 ? (
            <div className="divide-y divide-[var(--color-border)]">
              {documents.map((doc) => (
                <div key={doc.id} className={isMobile ? "px-3 py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between hover:bg-[var(--color-surface-muted)] transition-colors" : "px-6 py-4 flex items-center justify-between hover:bg-[var(--color-surface-muted)] transition-colors"}>
                  <div className={isMobile ? "flex items-start gap-2" : "flex items-center space-x-3"}>
                    <FileText className={isMobile ? "w-6 h-6 text-[var(--color-primary)] flex-shrink-0" : "w-8 h-8 text-[var(--color-primary)]"} />
                    <div>
                      <h3 className={isMobile ? "text-[var(--color-text)] font-medium text-sm truncate max-w-[140px]" : "text-[var(--color-text)] font-medium"} title={doc.original_filename || doc.filename}>
                        {formatDocumentDisplayName(doc.original_filename || doc.filename, {
                          documentType: doc.document_type,
                          documentId: doc.id,
                        })}
                      </h3>
                      <p className={isMobile ? "text-[var(--color-text-muted)] text-xs" : "text-[var(--color-text-muted)] text-sm"}>
                        {doc.document_type} • {new Date(doc.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className={isMobile ? "flex justify-between items-center mt-2 gap-2" : "flex items-center space-x-2"}>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      doc.status === 'completed' ? 'bg-[var(--color-success-soft)] text-[var(--color-success)]' :
                      doc.status === 'processing' ? 'bg-[var(--color-warning-soft)] text-[var(--color-warning)]' :
                      'bg-[var(--color-danger-soft)] text-[var(--color-danger)]'
                    }`}>
                      {doc.status}
                    </span>
                    <div className={isMobile ? "flex items-center gap-2" : "flex items-center space-x-2 ml-4"}>
                      <button
                        className={isMobile ? "bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white px-3 py-1 rounded-lg font-medium text-xs disabled:opacity-50" : "bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"}
                        onClick={() => handleSelectDocument(doc)}
                        disabled={deletingDocId === doc.id}
                      >
                        View
                      </button>
                      <button
                        type="button"
                        className={isMobile ? "p-2 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 disabled:opacity-50" : "p-2 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"}
                        onClick={() => handleDeleteDocument(doc)}
                        disabled={deletingDocId === doc.id}
                        aria-label={`Delete ${doc.original_filename || doc.filename}`}
                        title="Delete document"
                      >
                        {deletingDocId === doc.id ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={isMobile ? "px-3 py-8 text-center" : "px-6 py-12 text-center"}>
              <FileText className={isMobile ? "w-8 h-8 text-[var(--color-text-muted)] mx-auto mb-3" : "w-12 h-12 text-[var(--color-text-muted)] mx-auto mb-4"} />
              <p className={isMobile ? "text-[var(--color-text-muted)] text-sm" : "text-[var(--color-text-muted)]"}>No documents analyzed yet</p>
              <button
                onClick={() => setView('upload')}
                className={isMobile ? "mt-3 text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] font-medium text-sm" : "mt-4 text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] font-medium"}
              >
                Analyze your first document
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderAnalysisView = () => {
    if (!analysisResult) return null;

    // Support both new analysis and history: analysisResult.analysis or analysisResult.analysis_result
    const analysis = analysisResult.analysis || analysisResult.analysis_result || {};
    const summary = analysis.summary || 'No summary available.';
    const keyFindings = analysis.key_findings || analysis.risks || [];
    const strengths = analysis.strengths || [];
    const weaknesses = analysis.weaknesses || [];
    const suggestionsRaw = analysis.high_impact_suggestions || analysis.suggestions || [];
    const keywordAnalysis = analysis.keyword_analysis || null;
    const recommendations = analysis.recommendations || [];
    const score = analysis.score;
    const riskLevel = analysis.risk_level;
    const details = analysis.details;

    // NEW: richer fields mapping
    // Robust overall score to avoid NaN/0% visuals
    const overallScore = (() => {
      const candidates = [analysis.overall_score, analysis.overall_readiness, analysis.improvement_score, analysis.match_score];
      const first = candidates.find(v => typeof v === 'number' && isFinite(v));
      return typeof first === 'number' ? first : 0;
    })();
    const executiveSummary = analysis.executive_summary || summary;
    const dimensionScores = analysis.dimension_scores || {};
    const isContractDoc = (analysisResult.type || analysisResult.document_type || '').toString().toLowerCase() === 'contract';
    const isSopDoc = (analysisResult.type || analysisResult.document_type || '').toString().toLowerCase() === 'sop';
    const dimOrder = isContractDoc
      ? [
          { key: 'enforceability', label: 'Enforceability' },
          { key: 'fairness', label: 'Fairness' },
          { key: 'clarity', label: 'Clarity' },
          { key: 'rpa_compliance', label: 'Compliance' },
          { key: 'drafting_quality', label: 'Drafting' }
        ]
      : isSopDoc
      ? [
          { key: 'academic_alignment', label: 'Academic Alignment' },
          { key: 'technical_depth', label: 'Technical Depth' },
          { key: 'motivation_clarity', label: 'Motivation & Clarity' },
          { key: 'writing_quality', label: 'Writing Quality' },
          { key: 'overall_readiness', label: 'Overall Readiness' }
        ]
      : [
          { key: 'formatting', label: 'Formatting' },
          { key: 'technical', label: 'Technical' },
          { key: 'soft_skills', label: 'Soft Skills' },
          { key: 'progression', label: 'Progression' },
          { key: 'ats', label: 'ATS' }
        ];
    // SOP-safe dimension mapping (accept alternate keys)
    const getSopDimValue = (key) => {
      const ds = dimensionScores || {};
      const toNum = (v) => (typeof v === 'number' && isFinite(v) ? v : undefined);
      switch (key) {
        case 'academic_alignment':
          return toNum(ds.academic_alignment) ?? toNum(ds.academic) ?? toNum(ds.academic_fit);
        case 'technical_depth':
          return toNum(ds.technical_depth) ?? toNum(ds.technical);
        case 'motivation_clarity':
          return toNum(ds.motivation_clarity) ?? toNum(ds.narrative_clarity) ?? toNum(ds.motivation);
        case 'writing_quality':
          return toNum(ds.writing_quality) ?? toNum(ds.writing);
        case 'overall_readiness':
          return toNum(ds.overall_readiness) ?? toNum(analysis.overall_score) ?? toNum(analysis.improvement_score);
        default:
          return toNum(ds[key]);
      }
    };
    const dimData = dimOrder.map(d => ({
      ...d,
      value: isSopDoc ? (getSopDimValue(d.key) ?? 0) : (typeof dimensionScores[d.key] === 'number' ? dimensionScores[d.key] : 0)
    }));

    // Document info
    const docType = (analysisResult.type || analysisResult.document_type || 'Unknown').toLowerCase();
    const docTypeTitle = docType.charAt(0).toUpperCase() + docType.slice(1);
    const status = analysisResult.status || 'completed';
    const date = analysisResult.created_at ? new Date(analysisResult.created_at).toLocaleDateString() : '';
    const file = analysisResult.original_filename || analysisResult.filename || '';

    // Contract-specific derived fields
    const snapshot = isContractDoc ? (analysis.snapshot || {}) : {};
    const riskRegister = isContractDoc ? (analysis.risk_register || analysis.risks || []) : [];
    const clauseFixes = isContractDoc ? (analysis.clause_fixes || []) : [];
    const terminology = isContractDoc ? (analysis.terminology || []) : [];
    const deliverables = isContractDoc ? (analysis.deliverables || []) : [];
    const contractTabs = ['snapshot','risks','fixes','terms','deliverables'];
    const currentTabContract = isContractDoc ? (contractTabs.includes(analysisTab) ? analysisTab : 'snapshot') : analysisTab;

    // SOP-specific fields
    const programSnapshot = isSopDoc ? (analysis.program_snapshot || {}) : {};
    const sopDiagnostics = isSopDoc ? (analysis.sop_diagnostics || {}) : {};
    const sopTabs = ['course','structure','academic','writing','recommendations'];
    const cvTabs = ['insights', 'ats', 'match', 'actions', 'details'];
    const currentTab = isContractDoc ? (contractTabs.includes(analysisTab) ? analysisTab : 'snapshot') : (isSopDoc ? (sopTabs.includes(analysisTab) ? analysisTab : 'course') : (cvTabs.includes(analysisTab) ? analysisTab : 'insights'));

    // CV insight fields (Phase 1+)
    const isCvDoc = !isContractDoc && !isSopDoc;
    const scoreContext = isCvDoc ? (analysis.score_context || {}) : {};
    const insights = isCvDoc ? (analysis.insights || {}) : {};
    const atsDiagnostics = isCvDoc ? (analysis.ats_diagnostics || {}) : {};
    const jobMatch = isCvDoc ? (analysis.job_match || {}) : {};
    const skillsBreakdown = isCvDoc ? (analysis.skills_breakdown || {}) : {};
    const skillsDetected = skillsBreakdown.detected || {};
    const experienceSnapshot = isCvDoc ? (analysis.experience_snapshot || {}) : {};
    const completeness = isCvDoc ? (analysis.completeness || {}) : {};
    const actionPlan = isCvDoc && Array.isArray(analysis.action_plan) ? analysis.action_plan : [];
    const rewriteSuggestions = isCvDoc && Array.isArray(analysis.rewrite_suggestions) ? analysis.rewrite_suggestions : [];
    const bandLabel = String(scoreContext.band || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

    // Helper to render list items (string or object) — theme tokens keep light/dark readable
    const listTone = {
      green: { bullet: 'bg-[var(--color-success)]', border: 'border-[var(--color-success-border)]' },
      yellow: { bullet: 'bg-[var(--color-warning)]', border: 'border-[var(--color-warning-border)]' },
      blue: { bullet: 'bg-[var(--color-primary)]', border: 'border-[var(--color-primary)]/30' },
      red: { bullet: 'bg-[var(--color-danger)]', border: 'border-[var(--color-danger-border)]' },
      gray: { bullet: 'bg-[var(--color-text-muted)]', border: 'border-[var(--color-border)]' },
    };
    const renderList = (items, color = 'gray') => {
      if (!items || items.length === 0) return <div className="text-[var(--color-text-muted)] italic">None</div>;
      const tone = listTone[color] || listTone.gray;
      return (
        <ul className="space-y-2">
          {items.map((item, idx) =>
            typeof item === 'string' ? (
              <li key={idx} className="flex items-start text-[var(--color-text)]">
                <span className={`w-2 h-2 mt-2 mr-2 rounded-full ${tone.bullet} flex-shrink-0`}></span>
                <span>{item}</span>
              </li>
            ) : (
              <li key={idx} className={`p-3 rounded-lg bg-[var(--color-surface-muted)] border ${tone.border} mb-2`}>
                {item.type && <div className="font-semibold text-sm text-[var(--color-text-secondary)] mb-1">{item.type}</div>}
                {item.severity && <div className="text-xs text-[var(--color-warning)] mb-1">Severity: {item.severity}</div>}
                {item.description && <div className="text-[var(--color-text-secondary)] mb-1">{item.description}</div>}
                {item.recommendation && <div className="text-[var(--color-primary)] text-xs">Recommendation: {item.recommendation}</div>}
              </li>
            )
          )}
        </ul>
      );
    };

    // NEW: inline SVG radial progress for overall score
    const OverallScoreRing = ({ value }) => {
      const size = 110;
      const stroke = 10;
      const r = (size - stroke) / 2;
      const c = 2 * Math.PI * r;
      const pct = Math.max(0, Math.min(100, Number(value || 0)));
      const dash = (pct / 100) * c;
      return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={size/2} cy={size/2} r={r} stroke="var(--color-border)" strokeWidth={stroke} fill="none" />
          <circle
            cx={size/2}
            cy={size/2}
            r={r}
            stroke="url(#grad)"
            strokeWidth={stroke}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c-dash}`}
            transform={`rotate(-90 ${size/2} ${size/2})`}
          />
          <defs>
            <linearGradient id="grad" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--color-primary)" />
              <stop offset="100%" stopColor="var(--color-accent)" />
            </linearGradient>
          </defs>
          <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fill="var(--color-text)" fontSize="22" fontWeight="700">
            {Math.round(pct)}%
          </text>
        </svg>
      );
    };

    // NEW: inline SVG line chart for dimension scores
    const DimensionLineChart = ({ data }) => {
      const cleaned = data.map(d => ({ label: d.label, value: typeof d.value === 'number' ? d.value : 0 }));
      const width = 320; const height = 120; const pad = 24;
      const step = (width - pad * 2) / (cleaned.length - 1 || 1);
      const points = cleaned.map((d, i) => {
        const x = pad + i * step;
        const y = height - pad - (d.value / 100) * (height - pad * 2);
        return `${x},${y}`;
      }).join(' ');
      return (
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}> 
          <defs>
            <linearGradient id="lineGrad" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="var(--color-primary)" />
              <stop offset="100%" stopColor="var(--color-accent)" />
            </linearGradient>
          </defs>
          {/* axes */}
          <line x1={pad} y1={height-pad} x2={width-pad} y2={height-pad} stroke="var(--color-border)" />
          <line x1={pad} y1={pad} x2={pad} y2={height-pad} stroke="var(--color-border)" />
          {/* path */}
          <polyline points={points} fill="none" stroke="url(#lineGrad)" strokeWidth="3" />
          {/* dots and labels */}
          {cleaned.map((d, i) => {
            const x = pad + i * step;
            const y = height - pad - (d.value / 100) * (height - pad * 2);
            return (
              <g key={i}>
                <circle cx={x} cy={y} r="3.5" fill="var(--color-primary)" />
                <text x={x} y={height - 6} textAnchor="middle" fill="var(--color-text-muted)" fontSize="10">{d.label.replace(' ', '\n')}</text>
              </g>
            );
          })}
        </svg>
      );
    };

    // Main layout redesign with reduced padding and collapsible sections
    return (
      <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 py-8">
        {/* Header */}
        <div className="flex items-center mb-6">
          <button className="mr-4 p-2 rounded-full hover:bg-[var(--color-surface-muted)] transition-colors" onClick={() => setView('history')} aria-label="Back to Document History"><ArrowLeft className="w-6 h-6 text-[var(--color-text-muted)]" /></button>
          <h2 className="text-2xl font-bold text-[var(--color-text)]">{isContractDoc ? 'Contract Analysis' : isSopDoc ? 'SOP Analysis' : 'CV Analysis'}</h2>
          <button className="ml-auto brand-gradient-btn px-6 py-2 rounded-lg" onClick={() => navigate('/revamp-document')}>Revamp</button>
        </div>

        {/* Overview cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="elevated-card rounded-2xl p-6 flex items-center gap-4">
            <OverallScoreRing value={overallScore} />
            <div>
              <div className="text-sm text-[var(--color-text-muted)]">{isContractDoc ? 'Overall Compliance & Fairness' : isSopDoc ? 'Overall Quality' : 'Overall Score'}</div>
              <div className="text-3xl font-extrabold text-[var(--color-primary)]">{Math.round(overallScore)}%</div>
              {isCvDoc && bandLabel ? (
                <span className="inline-flex mt-2 px-2.5 py-1 rounded-lg text-xs font-semibold text-[var(--color-primary)] bg-[var(--color-primary-soft)] border border-[var(--color-primary)]/30">
                  {bandLabel}
                </span>
              ) : null}
              {isCvDoc && scoreContext.percentile_estimate ? (
                <div className="text-xs text-[var(--color-text-muted)] mt-2 leading-snug">{scoreContext.percentile_estimate}</div>
              ) : null}
            </div>
          </div>
          <div className="elevated-card rounded-2xl p-6 md:col-span-1">
            <div className="text-sm text-[var(--color-text-muted)] mb-1">{isCvDoc ? 'AI Verdict' : 'Executive Summary'}</div>
            <div className="text-[var(--color-text-secondary)]">
              {isCvDoc
                ? (scoreContext.one_line_verdict || insights.recommended_next_step || executiveSummary)
                : executiveSummary}
            </div>
          </div>
          {!isCvDoc ? (
            <div className="elevated-card rounded-2xl p-4">
              <div className="text-sm text-[var(--color-text-muted)] mb-1">Dimension Scores</div>
              <DimensionLineChart data={dimData} />
              <div className="mt-2 flex flex-wrap gap-2">
                {dimData.map(d => (<span key={d.key} className="px-2 py-1 rounded-full border border-[var(--color-border)] text-xs text-[var(--color-text-secondary)]">{d.label}: <span className="text-[var(--color-primary)] font-semibold ml-1">{Math.round(d.value)}%</span></span>))}
              </div>
            </div>
          ) : (
            <div className="elevated-card rounded-2xl p-6">
              <div className="text-sm text-[var(--color-text-muted)] mb-2">Quick signals</div>
              <div className="space-y-2 text-sm text-[var(--color-text-secondary)]">
                {typeof atsDiagnostics.score === 'number' && (
                  <div><span className="text-[var(--color-text-muted)]">ATS:</span> {Math.round(atsDiagnostics.score)}%</div>
                )}
                {typeof jobMatch.score === 'number' && (
                  <div><span className="text-[var(--color-text-muted)]">Job match:</span> {Math.round(jobMatch.score)}%</div>
                )}
                {insights.biggest_strength && (
                  <div className="text-[var(--color-text)] line-clamp-2 border-l-2 border-[var(--color-success)] pl-2"><span className="text-[var(--color-text-muted)]">Strength:</span> {insights.biggest_strength}</div>
                )}
                {insights.biggest_weakness && (
                  <div className="text-[var(--color-text)] line-clamp-2 border-l-2 border-[var(--color-warning)] pl-2"><span className="text-[var(--color-text-muted)]">Gap:</span> {insights.biggest_weakness}</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <>
          {isContractDoc ? (
            <div className="w-full mb-6">
              <div className="elevated-card rounded-2xl grid grid-cols-2 md:grid-cols-5 gap-2 p-2">
                {[
                  { id: 'snapshot', label: 'Snapshot' },
                  { id: 'risks', label: 'Risk Register' },
                  { id: 'fixes', label: 'Clause Fixes' },
                  { id: 'terms', label: 'Terminology' },
                  { id: 'deliverables', label: 'Deliverables' },
                ].map(t => (
                  <button key={t.id} onClick={() => setAnalysisTab(t.id)} className={`rounded-xl px-3 py-2 text-sm ${currentTab === t.id ? 'bg-[var(--color-primary)] text-white' : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]'}`}>{t.label}</button>
                ))}
              </div>
            </div>
          ) : isSopDoc ? (
            <div className="w-full mb-6">
              <div className="elevated-card rounded-2xl grid grid-cols-2 md:grid-cols-5 gap-2 p-2">
                {[
                  { id: 'course', label: 'Course Overview' },
                  { id: 'structure', label: 'Structure' },
                  { id: 'academic', label: 'Academic Fit' },
                  { id: 'writing', label: 'Writing Quality' },
                  { id: 'recommendations', label: 'Recommendations' },
                ].map(t => (
                  <button key={t.id} onClick={() => setAnalysisTab(t.id)} className={`rounded-xl px-3 py-2 text-sm ${currentTab === t.id ? 'bg-[var(--color-primary)] text-white' : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]'}`}>{t.label}</button>
                ))}
              </div>
            </div>
          ) : isCvDoc ? (
            <div className="w-full mb-6">
              <div className="elevated-card rounded-2xl grid grid-cols-2 md:grid-cols-5 gap-2 p-2">
                {[
                  { id: 'insights', label: 'Insights' },
                  { id: 'ats', label: 'ATS' },
                  { id: 'match', label: 'Match' },
                  { id: 'actions', label: 'Actions' },
                  { id: 'details', label: 'Details' },
                ].map(t => (
                  <button key={t.id} onClick={() => setAnalysisTab(t.id)} className={`rounded-xl px-3 py-2 text-sm ${currentTab === t.id ? 'bg-[var(--color-primary)] text-white' : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]'}`}>{t.label}</button>
                ))}
              </div>
            </div>
          ) : null}

          {/* SOP Tab Content */}
          {isSopDoc && currentTab === 'course' && (
            <div className="space-y-6">
              <div className="elevated-card rounded-2xl p-6">
                <div className="text-lg font-semibold text-[var(--color-text)] mb-1">Program Snapshot</div>
                <div className="text-xs text-[var(--color-text-muted)] mb-4">Extracted from SOP and context</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-[var(--color-text-secondary)]">
                  <div>
                    <ul className="list-disc pl-5">
                      {programSnapshot.program_type && (<li><span className="text-[var(--color-text-muted)]">Program Type:</span> {programSnapshot.program_type}</li>)}
                      {programSnapshot.institution && (<li><span className="text-[var(--color-text-muted)]">Institution:</span> {programSnapshot.institution}</li>)}
                      {programSnapshot.university && (<li><span className="text-[var(--color-text-muted)]">University:</span> {programSnapshot.university}</li>)}
                      {programSnapshot.university_location && (<li><span className="text-[var(--color-text-muted)]">Location:</span> {programSnapshot.university_location}</li>)}
                      {programSnapshot.country && (<li><span className="text-[var(--color-text-muted)]">Country:</span> {programSnapshot.country}</li>)}
                    </ul>
                  </div>
                  <div>
                    <ul className="list-disc pl-5">
                      {programSnapshot.course_of_study && (<li><span className="text-[var(--color-text-muted)]">Course of Study:</span> {programSnapshot.course_of_study}</li>)}
                      {programSnapshot.course_title && (<li><span className="text-[var(--color-text-muted)]">Course Title:</span> {programSnapshot.course_title}</li>)}
                      {programSnapshot.years_of_experience && (<li><span className="text-[var(--color-text-muted)]">Years of Experience:</span> {programSnapshot.years_of_experience}</li>)}
                      {programSnapshot.course_description && (<li><span className="text-[var(--color-text-muted)]">Course Description:</span> {programSnapshot.course_description}</li>)}
                    </ul>
                  </div>
                </div>

              {analysis.course_overview && (
                <div className="elevated-card rounded-2xl p-6">
                  <div className="text-lg font-semibold text-[var(--color-text)] mb-1">University & Course Overview</div>
                  <div className="text-xs text-[var(--color-text-muted)] mb-4">Context researched for alignment</div>
                  <div className="text-[var(--color-text-secondary)] text-sm leading-7">{analysis.course_overview}</div>
                </div>
              )}
              </div>

              {Array.isArray(recommendations) && recommendations.length > 0 && (
                <div className="elevated-card rounded-2xl p-6">
                  <div className="text-lg font-semibold text-[var(--color-text)] mb-1">Top Recommendations</div>
                  <div className="text-xs text-[var(--color-text-muted)] mb-4">Actionable steps to improve acceptance odds</div>
                  {renderList(recommendations, 'blue')}
                </div>
              )}
            </div>
          )}

          {isSopDoc && currentTab === 'structure' && (
            <div className="space-y-6">
              <div className="elevated-card rounded-2xl p-6">
                <div className="text-lg font-semibold text-[var(--color-text)] mb-1">SOP Structure Evaluation</div>
                <div className="text-xs text-[var(--color-text-muted)] mb-4">Assessment of flow, logic, and completeness.</div>
                <div className="text-[var(--color-text-secondary)] text-sm leading-7">{analysis.structure_evaluation || '—'}</div>
              </div>

              {((sopDiagnostics.passed || []).length > 0 || (sopDiagnostics.warnings || []).length > 0) && (
                <div className="elevated-card rounded-2xl p-6">
                  <div className="text-lg font-semibold text-[var(--color-text)] mb-1">SOP Diagnostics</div>
                  <div className="text-xs text-[var(--color-text-muted)] mb-4">
                    Deterministic checks against your SOP and program context
                  </div>
                  {typeof sopDiagnostics.score === 'number' && (
                    <div className="text-sm text-[var(--color-text-secondary)] mb-2">
                      <span className="text-[var(--color-text-muted)]">Diagnostics score:</span> {Math.round(sopDiagnostics.score)}/100
                    </div>
                  )}
                  {sopDiagnostics.signals?.word_count != null && (
                    <div className="text-sm text-[var(--color-text-secondary)] mb-3">
                      <span className="text-[var(--color-text-muted)]">Word count:</span> {sopDiagnostics.signals.word_count}
                    </div>
                  )}
                  {(sopDiagnostics.passed || []).length > 0 && (
                    <>
                      <div className="text-sm text-[var(--color-text-muted)] mb-1">Passed</div>
                      {renderList(sopDiagnostics.passed, 'green')}
                    </>
                  )}
                  {(sopDiagnostics.warnings || []).length > 0 && (
                    <>
                      <div className="text-sm text-[var(--color-text-muted)] mb-1 mt-3">Warnings</div>
                      {renderList(sopDiagnostics.warnings, 'yellow')}
                    </>
                  )}
                  {sopDiagnostics.specificity_notes && (
                    <div className="text-[var(--color-text-secondary)] text-sm leading-7 mt-3">{sopDiagnostics.specificity_notes}</div>
                  )}
                </div>
              )}
            </div>
          )}

          {isSopDoc && currentTab === 'academic' && (
            <div className="elevated-card rounded-2xl p-6">
              <div className="text-lg font-semibold text-[var(--color-text)] mb-1">Academic & Technical Fit</div>
              <div className="text-xs text-[var(--color-text-muted)] mb-4">Evaluating depth, relevance, and alignment with program objectives.</div>
              <div className="text-[var(--color-text-secondary)] text-sm leading-7">{analysis.academic_fit || '—'}</div>
            </div>
          )}

          {isSopDoc && currentTab === 'writing' && (
            <div className="elevated-card rounded-2xl p-6">
              <div className="text-lg font-semibold text-[var(--color-text)] mb-1">Writing & Communication Quality</div>
              <div className="text-xs text-[var(--color-text-muted)] mb-4">Tone, grammar, clarity, specificity, and examples.</div>
              <div className="text-[var(--color-text-secondary)] text-sm leading-7">{analysis.writing_quality_assessment || '—'}</div>
            </div>
          )}

          {isSopDoc && currentTab === 'recommendations' && (
            <div className="elevated-card rounded-2xl p-6">
              <div className="text-lg font-semibold text-[var(--color-text)] mb-1">Strategic Recommendations</div>
              <div className="text-xs text-[var(--color-text-muted)] mb-4">Expert suggestions for improvement and readiness.</div>
              {renderList(recommendations, 'gray')}
              {analysis.final_verdict && (
                <div className="text-[11px] text-[var(--color-text-muted)] mt-4">{analysis.final_verdict}</div>
              )}
            </div>
          )}

          {/* CV tabbed insights */}
          {isCvDoc && currentTab === 'insights' && (
            <div className="space-y-6">
              <div className="elevated-card rounded-2xl p-6">
                <div className="text-lg font-semibold text-[var(--color-text)] mb-1">AI Insights{bandLabel ? ` · ${bandLabel}` : ''}</div>
                {scoreContext.one_line_verdict && (
                  <div className="text-[var(--color-primary)] font-medium mb-2">{scoreContext.one_line_verdict}</div>
                )}
                <div className="text-[var(--color-text-secondary)] text-sm leading-7 mb-3">{insights.overall_assessment || executiveSummary}</div>
                <ul className="text-sm text-[var(--color-text-secondary)] space-y-2">
                  {insights.biggest_strength && (
                    <li><span className="text-[var(--color-text-muted)]">Biggest strength:</span> {insights.biggest_strength}</li>
                  )}
                  {insights.biggest_weakness && (
                    <li><span className="text-[var(--color-text-muted)]">Biggest gap:</span> {insights.biggest_weakness}</li>
                  )}
                  {insights.recommended_next_step && (
                    <li><span className="text-[var(--color-text-muted)]">Next step:</span> {insights.recommended_next_step}</li>
                  )}
                </ul>
              </div>
              {(experienceSnapshot.years_estimated || experienceSnapshot.seniority_signal || (experienceSnapshot.highlights || []).length > 0) && (
                <div className="elevated-card rounded-2xl p-6">
                  <div className="text-lg font-semibold text-[var(--color-text)] mb-3">Experience Snapshot</div>
                  <ul className="text-sm text-[var(--color-text-secondary)] space-y-1 mb-3">
                    {experienceSnapshot.years_estimated && (
                      <li><span className="text-[var(--color-text-muted)]">Years:</span> {experienceSnapshot.years_estimated}</li>
                    )}
                    {experienceSnapshot.seniority_signal && (
                      <li><span className="text-[var(--color-text-muted)]">Seniority:</span> {experienceSnapshot.seniority_signal}</li>
                    )}
                  </ul>
                  {(experienceSnapshot.highlights || []).length > 0 && (
                    <>
                      <div className="text-sm text-[var(--color-text-muted)] mb-1">Highlights</div>
                      {renderList(experienceSnapshot.highlights, 'green')}
                    </>
                  )}
                </div>
              )}
              {((completeness.present_sections || []).length || (completeness.missing_sections || []).length) > 0 && (
                <div className="elevated-card rounded-2xl p-6">
                  <div className="text-lg font-semibold text-[var(--color-text)] mb-3">Completeness</div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(completeness.present_sections || []).map((s) => (
                      <span key={s} className="px-2.5 py-1 rounded-lg text-xs font-semibold text-[var(--color-success)] bg-[var(--color-success-soft)] border border-[var(--color-success-border)]">{s}</span>
                    ))}
                  </div>
                  {(completeness.missing_sections || []).length > 0 && (
                    <>
                      <div className="text-sm text-[var(--color-text-muted)] mb-2">Missing</div>
                      <div className="flex flex-wrap gap-2">
                        {(completeness.missing_sections || []).map((s) => (
                          <span key={s} className="px-2.5 py-1 rounded-lg text-xs font-semibold text-[var(--color-warning)] bg-[var(--color-warning-soft)] border border-[var(--color-warning-border)]">{s}</span>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {isCvDoc && currentTab === 'ats' && (
            <div className="elevated-card rounded-2xl p-6">
              <div className="text-lg font-semibold text-[var(--color-text)] mb-1">ATS Diagnostics</div>
              {typeof atsDiagnostics.score === 'number' && (
                <div className="text-xs text-[var(--color-text-muted)] mb-3">Score {Math.round(atsDiagnostics.score)}%</div>
              )}
              {(atsDiagnostics.passed || []).length > 0 && (
                <>
                  <div className="text-sm text-[var(--color-success)] mb-1">Passed checks</div>
                  {renderList(atsDiagnostics.passed, 'green')}
                </>
              )}
              {(atsDiagnostics.warnings || []).length > 0 && (
                <div className="mt-3">
                  <div className="text-sm text-[var(--color-warning)] mb-1">Warnings</div>
                  {renderList(atsDiagnostics.warnings, 'yellow')}
                </div>
              )}
              {!(atsDiagnostics.passed || []).length && !(atsDiagnostics.warnings || []).length && (
                <div className="text-[var(--color-text-muted)]">No ATS diagnostics available for this result.</div>
              )}
            </div>
          )}

          {isCvDoc && currentTab === 'match' && (
            <div className="space-y-6">
              {(jobMatch.available !== false && (typeof jobMatch.score === 'number' || (jobMatch.matched_keywords || []).length || (jobMatch.missing_keywords || []).length)) ? (
                <div className="elevated-card rounded-2xl p-6">
                  <div className="text-lg font-semibold text-[var(--color-text)] mb-1">Job Match</div>
                  {typeof jobMatch.score === 'number' && (
                    <div className="text-xs text-[var(--color-text-muted)] mb-2">{Math.round(jobMatch.score)}% role fit</div>
                  )}
                  {jobMatch.notes && <div className="text-[var(--color-text-secondary)] text-sm mb-3">{jobMatch.notes}</div>}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-[var(--color-text-muted)] mb-2">Matched</div>
                      <div className="flex flex-wrap gap-2">
                        {(jobMatch.matched_keywords || []).map((k) => (
                          <span key={k} className="px-2.5 py-1 rounded-lg text-xs font-semibold text-[var(--color-success)] bg-[var(--color-success-soft)] border border-[var(--color-success-border)]">{k}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-[var(--color-text-muted)] mb-2">Missing</div>
                      <div className="flex flex-wrap gap-2">
                        {(jobMatch.missing_keywords || []).map((k) => (
                          <span key={k} className="px-2.5 py-1 rounded-lg text-xs font-semibold text-[var(--color-warning)] bg-[var(--color-warning-soft)] border border-[var(--color-warning-border)]">{k}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : keywordAnalysis && ((keywordAnalysis.matched_keywords || []).length || (keywordAnalysis.missing_keywords || []).length) ? (
                <div className="elevated-card rounded-2xl p-6">
                  <div className="text-lg font-semibold text-[var(--color-text)] mb-3">Keyword Analysis</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-[var(--color-text-muted)] mb-1">Matched</div>
                      {renderList(keywordAnalysis.matched_keywords || [], 'green')}
                    </div>
                    <div>
                      <div className="text-sm text-[var(--color-text-muted)] mb-1">Missing</div>
                      {renderList(keywordAnalysis.missing_keywords || [], 'yellow')}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="elevated-card rounded-2xl p-6 text-[var(--color-text-muted)]">
                  Add a job description when analyzing for detailed keyword match.
                </div>
              )}
              {((skillsDetected.technical || []).length || (skillsDetected.tools || []).length || (skillsBreakdown.missing_for_role || []).length) > 0 && (
                <div className="elevated-card rounded-2xl p-6">
                  <div className="text-lg font-semibold text-[var(--color-text)] mb-3">Skills Breakdown</div>
                  {(skillsDetected.technical || []).length > 0 && (
                    <div className="mb-3">
                      <div className="text-sm text-[var(--color-text-muted)] mb-1">Technical</div>
                      <div className="flex flex-wrap gap-2">
                        {skillsDetected.technical.map((s) => (
                          <span key={s} className="px-2.5 py-1 rounded-lg text-xs font-semibold text-[var(--color-primary)] bg-[var(--color-primary-soft)] border border-[var(--color-border)]">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(skillsDetected.tools || []).length > 0 && (
                    <div className="mb-3">
                      <div className="text-sm text-[var(--color-text-muted)] mb-1">Tools</div>
                      <div className="flex flex-wrap gap-2">
                        {skillsDetected.tools.map((s) => (
                          <span key={s} className="px-2.5 py-1 rounded-lg text-xs font-semibold text-[var(--color-primary)] bg-[var(--color-primary-soft)] border border-[var(--color-border)]">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(skillsBreakdown.missing_for_role || []).length > 0 && (
                    <div>
                      <div className="text-sm text-[var(--color-text-muted)] mb-1">Missing for role</div>
                      <div className="flex flex-wrap gap-2">
                        {skillsBreakdown.missing_for_role.map((s) => (
                          <span key={s} className="px-2.5 py-1 rounded-lg text-xs font-semibold text-[var(--color-warning)] bg-[var(--color-warning-soft)] border border-[var(--color-warning-border)]">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {isCvDoc && currentTab === 'actions' && (
            <div className="space-y-6">
              {actionPlan.length > 0 && (
                <div className="elevated-card rounded-2xl p-6">
                  <div className="text-lg font-semibold text-[var(--color-text)] mb-1">Action Plan</div>
                  <div className="text-xs text-[var(--color-text-muted)] mb-4">Prioritized next edits</div>
                  <div className="space-y-3">
                    {actionPlan.map((item, i) => (
                      <div key={i} className="p-3 rounded-lg bg-[var(--color-surface-muted)] border border-[var(--color-border)] flex gap-3">
                        <div className="mt-0.5 text-[var(--color-primary)]">○</div>
                        <div>
                          <div className="text-sm font-semibold text-[var(--color-text)]">
                            {String(item.priority || 'medium').toUpperCase()} — {item.action || item}
                          </div>
                          {item.why && <div className="text-[var(--color-text-secondary)] text-sm mt-1">{item.why}</div>}
                          {item.example && <div className="text-[var(--color-primary)] text-sm mt-1 whitespace-pre-wrap">{item.example}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {rewriteSuggestions.length > 0 && (
                <div className="elevated-card rounded-2xl p-6">
                  <div className="text-lg font-semibold text-[var(--color-text)] mb-3">Rewrite Suggestions</div>
                  <div className="space-y-3">
                    {rewriteSuggestions.map((item, i) => (
                      <div key={i} className="p-3 rounded-lg bg-[var(--color-surface-muted)] border border-[var(--color-border)]">
                        <div className="text-sm font-semibold text-[var(--color-text)] mb-1">{item.section || 'Suggestion'}</div>
                        {item.original && <div className="text-xs text-[var(--color-text-muted)] mb-1">Before: {item.original}</div>}
                        <div className="text-[var(--color-primary)] text-sm">After: {item.improved}</div>
                        {item.rationale && <div className="text-[var(--color-text-secondary)] text-sm mt-1">{item.rationale}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!actionPlan.length && !rewriteSuggestions.length && (
                <div className="elevated-card rounded-2xl p-6">
                  <div className="text-lg font-semibold text-[var(--color-text)] mb-3">Suggestions</div>
                  {renderList(suggestionsRaw || [], 'blue')}
                </div>
              )}
            </div>
          )}

          {isCvDoc && currentTab === 'details' && (
            <div className="space-y-6">
              <div className="elevated-card rounded-2xl p-6">
                <div className="text-lg font-semibold text-[var(--color-text)] mb-3">Dimension Scores</div>
                <DimensionLineChart data={dimData} />
                <div className="mt-3 flex flex-wrap gap-2">
                  {dimData.map(d => (
                    <span key={d.key} className="px-2 py-1 rounded-full border border-[var(--color-border)] text-xs text-[var(--color-text-secondary)]">
                      {d.label}: <span className="text-[var(--color-primary)] font-semibold ml-1">{Math.round(d.value)}%</span>
                    </span>
                  ))}
                </div>
              </div>
              {strengths.length > 0 && (
                <div className="elevated-card rounded-2xl p-6">
                  <div className="text-lg font-semibold text-[var(--color-text)] mb-3">Strengths</div>
                  {renderList(strengths, 'green')}
                </div>
              )}
              {weaknesses.length > 0 && (
                <div className="elevated-card rounded-2xl p-6">
                  <div className="text-lg font-semibold text-[var(--color-text)] mb-3">Areas to Improve</div>
                  {renderList(weaknesses, 'yellow')}
                </div>
              )}
              {(experienceSnapshot.gaps || []).length > 0 && (
                <div className="elevated-card rounded-2xl p-6">
                  <div className="text-lg font-semibold text-[var(--color-text)] mb-3">Experience Gaps</div>
                  {renderList(experienceSnapshot.gaps, 'yellow')}
                </div>
              )}
            </div>
          )}

          {/* Contract Tab Content */}
          {isContractDoc && currentTab === 'snapshot' && (
            <div className="space-y-6">
              <div className="elevated-card rounded-2xl p-6">
                <div className="text-lg font-semibold text-[var(--color-text)] mb-1">Parties, Premises & Jurisdiction</div>
                <div className="text-xs text-[var(--color-text-muted)] mb-4">What this agreement is and where it operates</div>
                {/* Map parties to legal roles */}
                {(() => {
                  const agreementType = (snapshot.agreement_type || '').toLowerCase();
                  const mapTitles = () => {
                    if (/(tenancy|lease)/.test(agreementType)) return ['Landlord', 'Tenant'];
                    if (/(employment|offer)/.test(agreementType)) return ['Employer', 'Employee'];
                    if (/(nda|non-disclosure|confidential)/.test(agreementType)) return ['Disclosing Party', 'Receiving Party'];
                    if (/(service|services|consult|contractor|freelance|msa|master services)/.test(agreementType)) return ['Client', 'Service Provider'];
                    if (/(loan)/.test(agreementType)) return ['Lender', 'Borrower'];
                    if (/(sale|purchase|supply|distribution)/.test(agreementType)) return ['Seller', 'Buyer'];
                    return ['Party 1', 'Party 2'];
                  };
                  const titles = mapTitles();
                  const parties = Array.isArray(snapshot.parties) ? snapshot.parties : [];
                  const partyLines = parties.map((p, i) => `${titles[i] || `Party ${i+1}`}: ${p}`);
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-[var(--color-text-secondary)]">
                      <div>
                        <div className="text-[var(--color-text-muted)] text-xs uppercase mb-1">Parties</div>
                        <ul className="list-disc pl-5">
                          {partyLines.length ? partyLines.map((t, i) => (<li key={i}>{t}</li>)) : (<li>—</li>)}
                        </ul>
                      </div>
                      <div>
                        <div className="text-[var(--color-text-muted)] text-xs uppercase mb-1">Contract Snapshot</div>
                        <ul className="list-disc pl-5">
                          {snapshot.agreement_type && (<li><span className="text-[var(--color-text-muted)]">Agreement Type:</span> {snapshot.agreement_type}</li>)}
                          {snapshot.jurisdiction && (<li><span className="text-[var(--color-text-muted)]">Jurisdiction:</span> {snapshot.jurisdiction}</li>)}
                          {(snapshot.contract_snapshot || []).map((p, i) => (<li key={i}>{p}</li>))}
                        </ul>
                      </div>
                    </div>
                  );
                })()}
              </div>
              {(snapshot.legal_benchmarks || []).length > 0 && (
                <div className="elevated-card rounded-2xl p-6">
                  <div className="text-lg font-semibold text-[var(--color-text)] mb-2">Legal Benchmarks (Applied)</div>
                  <ul className="list-disc pl-5 text-[var(--color-text-secondary)] text-sm">{snapshot.legal_benchmarks.map((b, i) => (<li key={i}>{b}</li>))}</ul>
                </div>
              )}
            </div>
          )}

          {isContractDoc && currentTab === 'risks' && (
            <div className="elevated-card rounded-2xl p-6">
              <div className="text-lg font-semibold text-[var(--color-text)] mb-1">Red Flags & Risks</div>
              <div className="text-xs text-[var(--color-text-muted)] mb-4">Traffic-light view</div>
              {Array.isArray(riskRegister) && riskRegister.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm table-fixed">
                    <colgroup>
                      <col className="w-[18%]" />
                      <col className="w-[30%]" />
                      <col className="w-[12%]" />
                      <col className="w-[40%]" />
                    </colgroup>
                    <thead>
                      <tr className="text-[var(--color-text-secondary)]">
                        <th className="text-left py-2 pr-6">Area</th>
                        <th className="text-left py-2 pr-6">Issue</th>
                        <th className="text-left py-2 pr-6">Severity</th>
                        <th className="text-left py-2">Why it matters</th>
                      </tr>
                    </thead>
                    <tbody className="text-[var(--color-text-secondary)]">
                      {riskRegister.map((r, i) => {
                        const sev = (r.severity || '').toString().toLowerCase();
                        const sevStyle = sev === 'critical' || sev === 'high'
                          ? 'bg-[var(--color-danger-soft)] text-[var(--color-danger)] ring-1 ring-[var(--color-danger-border)]'
                          : sev === 'medium'
                          ? 'bg-[var(--color-warning-soft)] text-[var(--color-warning)] ring-1 ring-[var(--color-warning-border)]'
                          : 'bg-[var(--color-success-soft)] text-[var(--color-success)] ring-1 ring-[var(--color-success-border)]';
                        return (
                          <tr key={i} className="border-t border-[var(--color-border)] align-top">
                            <td className="py-3 pr-6 text-[var(--color-text-secondary)]">{r.area || r.title || r.type || '—'}</td>
                            <td className="py-3 pr-6 truncate whitespace-nowrap overflow-ellipsis max-w-[18rem]">{r.issue || r.description || '—'}</td>
                            <td className="py-3 pr-6"><span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${sevStyle}`}>{r.severity || 'Medium'}</span></td>
                            <td className="py-3 whitespace-normal break-words leading-relaxed">{r.why || r.impact || r.reason || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (<div className="text-[var(--color-text-muted)]">No risks identified.</div>)}
            </div>
          )}

          {isContractDoc && currentTab === 'fixes' && (
            <div className="elevated-card rounded-2xl p-6">
              <div className="text-lg font-semibold text-[var(--color-text)] mb-1">Ready-to-Paste Clause Improvements</div>
              <div className="text-xs text-[var(--color-text-muted)] mb-4">Abuja-compliant drafting that balances both sides</div>
              {Array.isArray(clauseFixes) && clauseFixes.length > 0 ? (
                <div className="divide-y divide-gray-800">
                  {clauseFixes.map((c, i) => (
                    <details key={i} className="group">
                      <summary className="cursor-pointer select-none py-3 outline-none flex items-center justify-between text-[var(--color-text-secondary)] hover:text-[var(--color-text)]">
                        <span className="font-medium text-sm">{c.clause || `Clause ${i+1}`}</span>
                        <span className="text-[var(--color-text-muted)] group-open:rotate-180 transition-transform">▾</span>
                      </summary>
                      <div className="pb-4 text-sm">
                        {c.issue && (<div className="text-[var(--color-text-secondary)] mb-2"><span className="text-[var(--color-text-muted)]">Issue:</span> {c.issue}</div>)}
                        {c.fix && (<div className="text-[var(--color-primary)] whitespace-pre-wrap">{c.fix}</div>)}
                        {c.priority && (<div className="text-[var(--color-text-muted)] text-xs mt-2">Priority: {c.priority}</div>)}
                      </div>
                    </details>
                  ))}
                </div>
              ) : (<div className="text-[var(--color-text-muted)]">No clause fixes provided.</div>)}
            </div>
          )}

          {isContractDoc && currentTab === 'terms' && (
            <div className="elevated-card rounded-2xl p-6">
              <div className="text-lg font-semibold text-[var(--color-text)] mb-4">Terminology</div>
              {Array.isArray(terminology) && terminology.length > 0 ? (
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {terminology.map((t, i) => (
                    <div key={i} className="p-3 rounded-lg bg-[var(--color-surface-muted)] border border-[var(--color-border)]">
                      <dt className="text-[var(--color-text-secondary)] font-semibold">{t.term}</dt>
                      <dd className="text-[var(--color-text-secondary)] text-sm mt-1">{t.definition}</dd>
                    </div>
                  ))}
                </dl>
              ) : (<div className="text-[var(--color-text-muted)]">No terms provided.</div>)}
            </div>
          )}

          {isContractDoc && currentTab === 'deliverables' && (
            <div className="elevated-card rounded-2xl p-6">
              <div className="text-lg font-semibold text-[var(--color-text)] mb-4">Next Deliverables</div>
              {Array.isArray(deliverables) && deliverables.length > 0 ? (
                <ol className="list-decimal pl-5 text-[var(--color-text-secondary)] text-sm space-y-2">{deliverables.map((d, i) => (<li key={i}>{d}</li>))}</ol>
              ) : (<div className="text-[var(--color-text-muted)]">No deliverables listed.</div>)}
            </div>
          )}
        </>
      </div>
    );
  };

  const renderCurrentView = () => {
    switch (view) {
      case 'analysis':
        return renderAnalysisView();
      case 'history':
        return renderHistoryView();
      default:
        return renderUploadView();
    }
  };

  // Autofill guide statement for revamp modal
  useEffect(() => {
    if (showRevampModal) {
      let defaultGuideStatement = '';
      // Normalize document type
      const docTypeNorm = (documentType || '').toString().toLowerCase().replace(/^documenttype\./, '');
      // Get analysis result (analysisResult is in state)
      const analysis = analysisResult?.analysis || analysisResult?.analysis_result || {};
      if (docTypeNorm === 'cv') {
        defaultGuideStatement = 'Enhance action verbs and quantify achievements with metrics, improve keyword alignment with the target job role, optimize section structure and formatting, and strengthen skills presentation to increase ATS compatibility and recruiter appeal.';
      } else if (docTypeNorm === 'sop') {
        defaultGuideStatement = 'Strengthen the narrative flow, improve the achievements, and better connect goals with the program\'s objectives.';
      } else if (docTypeNorm === 'contract') {
        defaultGuideStatement = 'Focus on clarifying ambiguous terms, simplifying complex clauses, ensuring balanced risk allocation between parties, and improving enforceability while maintaining legal precision.';
      }
      // Append suggestions/recommendations if present
      if (Array.isArray(analysis.suggestions) && analysis.suggestions.length > 0) {
        defaultGuideStatement += '\nSuggestions: ' + analysis.suggestions.join('; ');
      }
      if (Array.isArray(analysis.recommendations) && analysis.recommendations.length > 0) {
        defaultGuideStatement += '\nRecommendations: ' + analysis.recommendations.join('; ');
      }
      // Only autofill if the user hasn't typed anything
      setRevampForm(f => ({ ...f, guide_statement: f.guide_statement || defaultGuideStatement }));
    }
  }, [showRevampModal, documentType, analysisResult]);

  return (
    <div className="h-full">
      {renderCurrentView()}
      <ProcessingDisclaimerModal
        open={disclaimerOpen}
        onConfirm={confirmProcessing}
        onClose={dismissDisclaimer}
        actionLabel={showRevampModal ? 'Revamp Document' : analyzeActionLabel}
      />
      {showRevampModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm animate-fadeIn p-2">
            <div className="elevated-card rounded-2xl p-0 w-full max-w-lg relative overflow-hidden animate-scaleIn">
              <div className="bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] px-8 pt-8 pb-4 flex flex-col items-center">
                <BarChart3 className="w-14 h-14 text-[var(--color-text)] mb-2 drop-shadow-lg" />
                <h2 className="text-2xl font-bold text-[var(--color-text)] mb-1">Revamp Document</h2>
              </div>
              <button
                className="absolute top-4 right-4 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                onClick={() => setShowRevampModal(false)}
              >
                <X className="w-6 h-6" />
              </button>
              <form className="space-y-4 px-8 pb-8 pt-4" onSubmit={e => { e.preventDefault(); requestProcessing(handleRevamp); }}>
                <div className="flex flex-col gap-4">
                  {/* Purpose only for CV */}
                  {documentType === 'cv' && (
                    <>
                      <label className="text-[var(--color-text-secondary)] font-medium">Purpose</label>
                      <FormSelect
                        value={revampForm.purpose}
                        onChange={(v) => setRevampForm(f => ({ ...f, purpose: v }))}
                        options={PURPOSE_OPTIONS}
                        placeholder="Select purpose"
                      />
                    </>
                  )}
                  {/* Guide Statement for all */}
                  <label className="text-[var(--color-text-secondary)] font-medium">Guide Statement (optional)</label>
                  <textarea
                    className="bg-[var(--color-input-bg)] text-[var(--color-text)] rounded-lg px-4 py-2 border border-[var(--color-border)] focus:ring-2 focus:ring-[var(--color-ring)]"
                    value={revampForm.guide_statement}
                    onChange={e => setRevampForm(f => ({ ...f, guide_statement: e.target.value }))}
                    placeholder={
                      documentType === 'cv'
                        ? 'E.g., Enhance action verbs, quantify achievements with metrics, and improve readability while maintaining professional tone.'
                        : documentType === 'sop'
                        ? 'E.g., Strengthen the narrative flow, add specific examples of achievements, and better connect goals with the program objectives.'
                        : 'E.g., Focus on clarifying ambiguous terms, simplifying complex clauses, and ensuring balanced risk allocation between parties.'
                    }
                  />
                  {/* SOP extra fields */}
                  {documentType === 'sop' && (
                    <>
                      <label className="text-[var(--color-text-secondary)] font-medium">Program Type</label>
                      <FormSelect
                        value={revampForm.program_type}
                        onChange={(v) => setRevampForm(f => ({ ...f, program_type: v }))}
                        options={PROGRAM_TYPE_OPTIONS}
                        placeholder="Select program type"
                      />
                      <label className="text-[var(--color-text-secondary)] font-medium">University</label>
                      <input
                        className="bg-[var(--color-input-bg)] text-[var(--color-text)] rounded-lg px-4 py-2 border border-[var(--color-border)] focus:ring-2 focus:ring-[var(--color-ring)]"
                        value={revampForm.university}
                        onChange={e => setRevampForm(f => ({ ...f, university: e.target.value }))}
                        placeholder="e.g., Harvard University"
                        required
                      />
                      <label className="text-[var(--color-text-secondary)] font-medium">Country</label>
                      <FormSelect
                        value={revampForm.country}
                        onChange={(v) => setRevampForm((f) => ({ ...f, country: v }))}
                        options={COUNTRY_OPTIONS}
                        placeholder="Search and select country"
                        searchable
                      />
                      <label className="text-[var(--color-text-secondary)] font-medium">Course Title</label>
                      <input
                        className="bg-[var(--color-input-bg)] text-[var(--color-text)] rounded-lg px-4 py-2 border border-[var(--color-border)] focus:ring-2 focus:ring-[var(--color-ring)]"
                        value={revampForm.course_title}
                        onChange={e => setRevampForm(f => ({ ...f, course_title: e.target.value }))}
                        placeholder="e.g., Computer Science"
                        required
                      />
                      <label className="text-[var(--color-text-secondary)] font-medium">Course Description</label>
                      <RichTextEditor
                        value={revampForm.course_description}
                        onChange={(html) => setRevampForm(f => ({ ...f, course_description: html }))}
                        placeholder="Paste the official course description here..."
                        minHeight={180}
                      />
                    </>
                  )}
                  {/* Tone dropdown for Contract only */}
                  {documentType === 'contract' && (
                    <>
                      <label className="text-[var(--color-text-secondary)] font-medium">Tone</label>
                      <select
                        className="bg-[var(--color-input-bg)] text-[var(--color-text)] rounded-lg px-4 py-2 border border-[var(--color-border)] focus:ring-2 focus:ring-[var(--color-ring)]"
                        value={revampForm.style}
                        onChange={e => setRevampForm(f => ({ ...f, style: e.target.value }))}
                        required
                      >
                        <option value="">Select tone</option>
                        <option value="Formal">Formal</option>
                        <option value="Concise">Concise</option>
                        <option value="Legal">Legal</option>
                        <option value="Friendly">Friendly</option>
                        <option value="Impactful">Impactful</option>
                        <option value="Professional">Professional</option>
                        <option value="Clear">Clear</option>
                      </select>
                    </>
                  )}
                  {/* Optional style/tone for CV/SOP only */}
                  {(documentType === 'cv' || documentType === 'sop') && (
                    <>
                      <label className="text-[var(--color-text-secondary)] font-medium">Style/Tone (optional)</label>
                      <input
                        className="bg-[var(--color-input-bg)] text-[var(--color-text)] rounded-lg px-4 py-2 border border-[var(--color-border)] focus:ring-2 focus:ring-[var(--color-ring)]"
                        value={revampForm.style}
                        onChange={e => setRevampForm(f => ({ ...f, style: e.target.value }))}
                        placeholder="e.g., Formal, Concise, Impactful"
                      />
                    </>
                  )}
                  {revampError && <div className="text-red-400 text-sm mt-2">{revampError}</div>}
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] text-white px-6 py-2 rounded-lg font-semibold hover:opacity-90 disabled:opacity-60 mt-4"
                    disabled={revampLoading}
                  >
                    {revampLoading ? 'Revamping...' : 'Revamp Now'}
                  </button>
                </div>
              </form>
            </div>
            <style>{`
              .animate-fadeIn { animation: fadeInModal 0.25s ease; }
              .animate-scaleIn { animation: scaleInModal 0.25s cubic-bezier(.4,2,.6,1); }
              @keyframes fadeInModal { from { opacity: 0; } to { opacity: 1; } }
              @keyframes scaleInModal { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
            `}</style>
          </div>
        )}
      <ConfirmModal
        open={Boolean(docToDelete)}
        title="Delete document?"
        message={
          docToDelete
            ? `Delete "${formatDocumentDisplayName(docToDelete.original_filename || docToDelete.filename, {
                documentType: docToDelete.document_type,
                documentId: docToDelete.id,
              })}"? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
        onCancel={() => setDocToDelete(null)}
        onConfirm={confirmDeleteDocument}
      />
    </div>
  );
};

export default DocumentAnalyzer;
